import React from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, MailingAddressFields } from '../../../components';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';

import css from './SignupForm.module.css';
import '../../../styles/react-phone.css';
import { checkDisposableEmail } from '../../../ducks/global.duck';
import { useDispatch } from 'react-redux';
import { createEmptyMailingAddress } from '../../../util/mailingAddress';

const siteKey = process.env.REACT_APP_SITE_KEY;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{
      ...(props.initialValues || {}),
      mailingAddress: {
        ...createEmptyMailingAddress(),
        ...(props.initialValues?.mailingAddress || {}),
      },
    }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        form,
        values,
        signUpMessage,
      } = fieldRenderProps;
      const dispatch = useDispatch();
      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      const disposableEmailInvalid = async value => {
        if (!value) return undefined;
        const email = value.trim();
        try {
          const data = await dispatch(checkDisposableEmail({ email }));
          if (data?.disposable === 'true') {
            return intl.formatMessage({
              id: 'SignupForm.disposableEmailInvalid',
            });
          }
          return undefined;
        } catch (e) {
          return undefined;
        }
      };

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Phone number
      const phoneNumberLabel = intl.formatMessage({
        id: 'SignupForm.phoneNumberLabel',
      });
      const phoneNumberPlaceholder = intl.formatMessage({
        id: 'SignupForm.phoneNumberPlaceholder',
      });

      // CAPTCHA
      const captchaRequiredMessage = intl.formatMessage({
        id: 'SignupForm.captchaRequired',
      });
      const captchaRequired = validators.required(captchaRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const { phoneNumber, invalidPhoneValue } = values || {};
      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={intl.formatMessage({
                id: 'SignupForm.emailLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.emailPlaceholder',
              })}
              validate={validators.composeValidators(
                emailRequired,
                emailValid,
                disposableEmailInvalid
              )}
            />
            <div className={css.name}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.fname` : 'fname'}
                name="fname"
                autoComplete="given-name"
                label={intl.formatMessage({
                  id: 'SignupForm.firstNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.firstNamePlaceholder',
                })}
                validate={validators.composeValidators(
                  validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  ),
                  validators.requiredAlpha(
                    intl.formatMessage({
                      id: 'SignupForm.alphaRequired',
                    })
                  )
                )}
              />
              <FieldTextInput
                className={css.lastNameRoot}
                type="text"
                id={formId ? `${formId}.lname` : 'lname'}
                name="lname"
                autoComplete="family-name"
                label={intl.formatMessage({
                  id: 'SignupForm.lastNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.lastNamePlaceholder',
                })}
                validate={validators.composeValidators(
                  validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  ),
                  validators.requiredAlpha(
                    intl.formatMessage({
                      id: 'SignupForm.alphaRequired',
                    })
                  )
                )}
              />
            </div>
            <div className={css.phoneNumberWrapper}>
              <label className={css.phoneNumberLabel}>{phoneNumberLabel}</label>
              <Field name="phoneNumber">
                {({ input }) => (
                  <div>
                    <PhoneInput
                      {...input}
                      format={formatPhoneNumberIntl}
                      className={css.fieldTextInput}
                      placeholder={phoneNumberPlaceholder}
                      defaultCountry="US"
                      onChange={value => {
                        input.onChange(value); // Ensure the form field value is updated
                        const formattedNumber = formatPhoneNumberIntl(value);
                        if (isValidPhoneNumber(formattedNumber)) {
                          form.change('invalidPhoneValue', false);
                        } else {
                          form.change('invalidPhoneValue', true);
                        }
                      }}
                    />
                  </div>
                )}
              </Field>
            </div>
            {invalidPhoneValue && phoneNumber && (
              <p className={css.invalidPhoneValue}>
                <FormattedMessage id="SignupForm.invalidPhoneNumber" />
              </p>
            )}
            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="new-password"
              label={intl.formatMessage({
                id: 'SignupForm.passwordLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.passwordPlaceholder',
              })}
              validate={passwordValidators}
            />
            {/* <div className={css.customFields}>
              {userFieldProps.map(fieldProps => (
                <CustomExtendedDataField {...fieldProps} formId={formId} />
              ))}
            </div> */}
          </div>
          <FieldTextInput
            className={css.password}
            type="text"
            id={formId ? `${formId}.referralCode` : 'referralCode'}
            name="referralCode"
            label={intl.formatMessage({
              id: 'SignupForm.referralCode',
            })}
            placeholder={intl.formatMessage({
              id: 'SignupForm.referralCodePlaceholder',
            })}
          />
          <section className={css.mailingAddressSection} aria-labelledby="signup-mailing-address">
            <h2 id="signup-mailing-address" className={css.sectionTitle}>
              {intl.formatMessage({ id: 'SectionMailingAddress.heading' })}
            </h2>
            <p className={css.mailingAddressInfo}>
              {intl.formatMessage({ id: 'SectionMailingAddress.privateNote' })}
            </p>
            <MailingAddressFields form={form} formId={formId || 'SignupForm'} values={values} />
          </section>
          <div className={css.captchaWrapper}>
            <Field name="captchaToken" validate={captchaRequired}>
              {({ input, meta }) => (
                <>
                  <ReCAPTCHA sitekey={siteKey} onChange={value => input.onChange(value)} />
                  {meta.touched && meta.error && <p className={css.error}>{meta.error}</p>}
                </>
              )}
            </Field>
          </div>

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            {signUpMessage}
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
